import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import * as api from "../../services/api";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = String(d).split("-");
  return `${day}/${m}/${y}`;
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const CATEGORIAS = [
  { value: "alquiler", label: "Alquiler" },
  { value: "sueldos", label: "Sueldos" },
  { value: "equipamiento", label: "Equipamiento" },
  { value: "servicios", label: "Servicios" },
  { value: "marketing", label: "Marketing" },
  { value: "otro", label: "Otro" },
];

const CAT_COLOR = {
  alquiler: "#6366f1",
  sueldos: "#f59e0b",
  equipamiento: "#10b981",
  servicios: "#3b82f6",
  marketing: "#ec4899",
  otro: "#94a3b8",
};

// ── sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color, positive }) {
  const isNeg = typeof positive === "boolean" ? !positive : value < 0;
  return (
    <div
      className="relative overflow-hidden rounded-xl border bg-slate-900/60 p-5"
      style={{ borderColor: `${color}33` }}
    >
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: color }} />
      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-bold ${isNeg ? "text-red-400" : "text-white"}`}>{fmt(value)}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function MiniBar({ label, ingresos, egresos, maxVal, color }) {
  const pct = (v) => (maxVal > 0 ? Math.round((v / maxVal) * 100) : 0);
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-16 text-slate-400 text-right shrink-0">{label}</span>
      <div className="flex-1 space-y-0.5">
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct(ingresos)}%`, background: color }} />
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-red-500/60" style={{ width: `${pct(egresos)}%` }} />
        </div>
      </div>
      <span className="w-20 text-right text-slate-300">{fmt(ingresos - egresos)}</span>
    </div>
  );
}

function CatPill({ cat, total, color }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-slate-800/50 px-3 py-2 text-sm">
      <span className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <span className="text-slate-300 capitalize">{cat}</span>
      </span>
      <span className="text-white font-semibold">{fmt(total)}</span>
    </div>
  );
}

function FormModal({ title, onClose, onSubmit, submitting, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-white font-semibold">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">✕</button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          className="p-5 space-y-4"
        >
          {children}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:border-slate-400 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 transition-colors"
              style={{ background: "#6366f1" }}
            >
              {submitting ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none";

// ── export CSV ───────────────────────────────────────────────────────────────

function exportCSV(ingresos, egresos, mesLabel) {
  const rows = [
    ["Tipo", "Fecha", "Descripción", "Categoría", "Monto"],
    ...ingresos.map((i) => ["Ingreso", fmtDate(i.fecha), i.descripcion || i.tipo_display, "", i.monto]),
    ...egresos.map((e) => ["Egreso", fmtDate(e.fecha), e.descripcion || e.categoria_display, e.categoria_display, `-${e.monto}`]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `contabilidad_${mesLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── main component ────────────────────────────────────────────────────────────

export function ContabilidadSection({ primaryColor = "#6366f1" }) {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);

  const [resumen, setResumen] = useState(null);
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [loading, setLoading] = useState(true);

  // modals
  const [showIngreso, setShowIngreso] = useState(false);
  const [showEgreso, setShowEgreso] = useState(false);
  const [editEgreso, setEditEgreso] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const emptyIng = { monto: "", descripcion: "", fecha: hoy.toISOString().slice(0, 10) };
  const emptyEgr = { monto: "", categoria: "otro", descripcion: "", fecha: hoy.toISOString().slice(0, 10) };
  const [formIng, setFormIng] = useState(emptyIng);
  const [formEgr, setFormEgr] = useState(emptyEgr);

  // vista activa en tabla
  const [tablaVista, setTablaVista] = useState("ingresos");

  const cargar = useCallback(async () => {
    setLoading(true);
    const [res, ing, egr] = await Promise.all([
      api.getContabilidadResumen(anio, mes),
      api.listarIngresos(anio, mes),
      api.listarEgresos(anio, mes),
    ]);
    setResumen(res?.error ? null : res);
    setIngresos(Array.isArray(ing) ? ing : []);
    setEgresos(Array.isArray(egr) ? egr : []);
    setLoading(false);
  }, [anio, mes]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── handlers ───────────────────────────────────────────────────────────────

  const handleCrearIngreso = async () => {
    if (!formIng.monto) return toast.error("El monto es requerido");
    setSubmitting(true);
    const res = await api.crearIngreso(formIng);
    setSubmitting(false);
    if (res?.error) return toast.error(res.error);
    toast.success("Ingreso registrado");
    setShowIngreso(false);
    setFormIng(emptyIng);
    cargar();
  };

  const handleCrearEgreso = async () => {
    if (!formEgr.monto) return toast.error("El monto es requerido");
    setSubmitting(true);
    const res = await api.crearEgreso(formEgr);
    setSubmitting(false);
    if (res?.error) return toast.error(res.error);
    toast.success("Egreso registrado");
    setShowEgreso(false);
    setFormEgr(emptyEgr);
    cargar();
  };

  const handleEditarEgreso = async () => {
    if (!formEgr.monto) return toast.error("El monto es requerido");
    setSubmitting(true);
    const res = await api.editarEgreso(editEgreso.id, formEgr);
    setSubmitting(false);
    if (res?.error) return toast.error(res.error);
    toast.success("Egreso actualizado");
    setEditEgreso(null);
    cargar();
  };

  const handleEliminarEgreso = async (id) => {
    if (!window.confirm("¿Eliminar este egreso?")) return;
    const res = await api.eliminarEgreso(id);
    if (res?.error) return toast.error(res.error);
    toast.success("Egreso eliminado");
    cargar();
  };

  const handleEliminarIngreso = async (id) => {
    if (!window.confirm("¿Eliminar este ingreso?")) return;
    const res = await api.eliminarIngreso(id);
    if (res?.error) return toast.error(res.error);
    toast.success("Ingreso eliminado");
    cargar();
  };

  const abrirEditEgreso = (e) => {
    setFormEgr({
      monto: String(e.monto),
      categoria: e.categoria,
      descripcion: e.descripcion || "",
      fecha: e.fecha,
    });
    setEditEgreso(e);
  };

  // ── navegación mes ─────────────────────────────────────────────────────────

  const mesAnterior = () => {
    if (mes === 1) { setMes(12); setAnio(a => a - 1); }
    else setMes(m => m - 1);
  };
  const mesSiguiente = () => {
    if (mes === 12) { setMes(1); setAnio(a => a + 1); }
    else setMes(m => m + 1);
  };

  const mesLabel = `${MESES[mes - 1]} ${anio}`;
  const maxEvol = resumen?.evolucion
    ? Math.max(...resumen.evolucion.map((m) => Math.max(m.ingresos, m.egresos)), 1)
    : 1;

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Encabezado + navegación de mes */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white">Contabilidad</h2>
          <p className="text-sm text-slate-400">Control de ingresos y egresos del gimnasio</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={mesAnterior}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-slate-500 transition-colors"
          >
            ‹
          </button>
          <span className="text-white font-medium text-sm w-36 text-center">{mesLabel}</span>
          <button
            onClick={mesSiguiente}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-slate-500 transition-colors"
          >
            ›
          </button>
          <button
            onClick={() => exportCSV(ingresos, egresos, `${MESES[mes - 1]}_${anio}`)}
            className="rounded-lg border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:text-white hover:border-slate-400 transition-colors"
          >
            ↓ CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Cargando...</div>
      ) : (
        <>
          {/* Tarjetas resumen del mes */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard
              label="Ingresos del mes"
              value={resumen?.mes?.ingresos ?? 0}
              sub={`${ingresos.length} registro${ingresos.length !== 1 ? "s" : ""}`}
              color="#10b981"
              positive={true}
            />
            <SummaryCard
              label="Egresos del mes"
              value={resumen?.mes?.egresos ?? 0}
              sub={`${egresos.length} registro${egresos.length !== 1 ? "s" : ""}`}
              color="#ef4444"
              positive={true}
            />
            <SummaryCard
              label="Balance neto"
              value={resumen?.mes?.balance ?? 0}
              sub={`Año: ${fmt(resumen?.anio?.balance ?? 0)}`}
              color={primaryColor}
            />
          </div>

          {/* Gráfico de barras simple + categorías */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Evolución 6 meses */}
            <div className="lg:col-span-2 rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <p className="text-sm font-medium text-white mb-4">Evolución últimos 6 meses</p>
              <div className="space-y-3">
                {resumen?.evolucion?.map((m) => (
                  <MiniBar
                    key={m.mes}
                    label={m.mes.split(" ")[0].slice(0, 3)}
                    ingresos={m.ingresos}
                    egresos={m.egresos}
                    maxVal={maxEvol}
                    color={primaryColor}
                  />
                ))}
              </div>
              <div className="flex gap-4 mt-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-full" style={{ background: primaryColor }} />Ingresos</span>
                <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-full bg-red-500/60" />Egresos</span>
              </div>
            </div>

            {/* Egresos por categoría */}
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <p className="text-sm font-medium text-white mb-4">Egresos por categoría</p>
              {resumen?.egresos_por_categoria?.length > 0 ? (
                <div className="space-y-2">
                  {resumen.egresos_por_categoria.map((c) => (
                    <CatPill
                      key={c.categoria}
                      cat={c.categoria}
                      total={c.total}
                      color={CAT_COLOR[c.categoria] || "#94a3b8"}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">Sin egresos este mes</p>
              )}
            </div>
          </div>

          {/* Acciones */}
          <div className="flex gap-3">
            <button
              onClick={() => { setFormIng(emptyIng); setShowIngreso(true); }}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
              style={{ background: "#10b981" }}
            >
              + Ingreso manual
            </button>
            <button
              onClick={() => { setFormEgr(emptyEgr); setShowEgreso(true); }}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-500 transition-colors"
            >
              + Egreso
            </button>
          </div>

          {/* Tabs ingresos / egresos */}
          <div className="rounded-xl border border-slate-700 bg-slate-900/60 overflow-hidden">
            <div className="flex border-b border-slate-700">
              {["ingresos", "egresos"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTablaVista(t)}
                  className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                    tablaVista === t
                      ? "text-white border-b-2"
                      : "text-slate-400 hover:text-white"
                  }`}
                  style={tablaVista === t ? { borderColor: primaryColor } : {}}
                >
                  {t} ({t === "ingresos" ? ingresos.length : egresos.length})
                </button>
              ))}
            </div>

            {tablaVista === "ingresos" ? (
              <div className="overflow-x-auto">
                {ingresos.length === 0 ? (
                  <p className="text-center text-slate-500 py-8 text-sm">Sin ingresos este mes</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 text-xs text-slate-400 uppercase">
                        <th className="px-4 py-3 text-left">Fecha</th>
                        <th className="px-4 py-3 text-left">Descripción</th>
                        <th className="px-4 py-3 text-left">Tipo</th>
                        <th className="px-4 py-3 text-right">Monto</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {ingresos.map((i) => (
                        <tr key={i.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3 text-slate-300">{fmtDate(i.fecha)}</td>
                          <td className="px-4 py-3 text-white">{i.descripcion || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              i.tipo === "suscripcion"
                                ? "bg-indigo-500/20 text-indigo-300"
                                : "bg-slate-700 text-slate-300"
                            }`}>
                              {i.tipo_display}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-400 font-semibold">{fmt(i.monto)}</td>
                          <td className="px-4 py-3 text-right">
                            {i.tipo === "manual" && (
                              <button
                                onClick={() => handleEliminarIngreso(i.id)}
                                className="text-slate-500 hover:text-red-400 transition-colors text-xs"
                              >
                                Eliminar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                {egresos.length === 0 ? (
                  <p className="text-center text-slate-500 py-8 text-sm">Sin egresos este mes</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 text-xs text-slate-400 uppercase">
                        <th className="px-4 py-3 text-left">Fecha</th>
                        <th className="px-4 py-3 text-left">Descripción</th>
                        <th className="px-4 py-3 text-left">Categoría</th>
                        <th className="px-4 py-3 text-right">Monto</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {egresos.map((e) => (
                        <tr key={e.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3 text-slate-300">{fmtDate(e.fecha)}</td>
                          <td className="px-4 py-3 text-white">{e.descripcion || "—"}</td>
                          <td className="px-4 py-3">
                            <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-slate-700 text-slate-300"
                              style={{ borderLeft: `3px solid ${CAT_COLOR[e.categoria] || "#94a3b8"}` }}>
                              {e.categoria_display}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-red-400 font-semibold">{fmt(e.monto)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => abrirEditEgreso(e)}
                                className="text-slate-400 hover:text-white transition-colors text-xs"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleEliminarEgreso(e.id)}
                                className="text-slate-500 hover:text-red-400 transition-colors text-xs"
                              >
                                Eliminar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal — Ingreso manual */}
      {showIngreso && (
        <FormModal
          title="Registrar ingreso manual"
          onClose={() => setShowIngreso(false)}
          onSubmit={handleCrearIngreso}
          submitting={submitting}
        >
          <Field label="Monto ($)">
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputCls}
              placeholder="0.00"
              value={formIng.monto}
              onChange={(e) => setFormIng((p) => ({ ...p, monto: e.target.value }))}
            />
          </Field>
          <Field label="Descripción">
            <input
              type="text"
              className={inputCls}
              placeholder="Ej: Venta de indumentaria"
              value={formIng.descripcion}
              onChange={(e) => setFormIng((p) => ({ ...p, descripcion: e.target.value }))}
            />
          </Field>
          <Field label="Fecha">
            <input
              type="date"
              className={inputCls}
              value={formIng.fecha}
              onChange={(e) => setFormIng((p) => ({ ...p, fecha: e.target.value }))}
            />
          </Field>
        </FormModal>
      )}

      {/* Modal — Egreso */}
      {(showEgreso || editEgreso) && (
        <FormModal
          title={editEgreso ? "Editar egreso" : "Registrar egreso"}
          onClose={() => { setShowEgreso(false); setEditEgreso(null); }}
          onSubmit={editEgreso ? handleEditarEgreso : handleCrearEgreso}
          submitting={submitting}
        >
          <Field label="Monto ($)">
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputCls}
              placeholder="0.00"
              value={formEgr.monto}
              onChange={(e) => setFormEgr((p) => ({ ...p, monto: e.target.value }))}
            />
          </Field>
          <Field label="Categoría">
            <select
              className={inputCls}
              value={formEgr.categoria}
              onChange={(e) => setFormEgr((p) => ({ ...p, categoria: e.target.value }))}
            >
              {CATEGORIAS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Descripción">
            <input
              type="text"
              className={inputCls}
              placeholder="Ej: Alquiler del local agosto"
              value={formEgr.descripcion}
              onChange={(e) => setFormEgr((p) => ({ ...p, descripcion: e.target.value }))}
            />
          </Field>
          <Field label="Fecha">
            <input
              type="date"
              className={inputCls}
              value={formEgr.fecha}
              onChange={(e) => setFormEgr((p) => ({ ...p, fecha: e.target.value }))}
            />
          </Field>
        </FormModal>
      )}
    </div>
  );
}
